import { useLocale, usePowerLevelsRules } from '@e2m-customer-portal/hooks';
import { NullableMeasure, convertMeasure, getMonthNumber, parseInteger } from '@e2m-customer-portal/shared';
import { AbortConfirmationDialog } from '@e2m-customer-portal/ui';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Interval } from 'date-fns';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DefaultForecast, DefaultForecastInput } from '../../../generated/graphql';
import { SaveBar } from '../short-term-forecast/save-bar/SaveBar';
import { AnnualDeviceTableCell } from './AnnualDeviceTableCell';
import { useDefaultForecastMutation } from './queries';
import { useDefaultTableRules } from './utils';

enum DataPoint {
  maximum = 0,
  minimum = 1,
  auxiliaryConsumption = 2,
  production = 3,
}

function deepClone<T>(matrix: Array<Array<T>>): Array<Array<T>> {
  return matrix.map((row) => row.slice());
}

function mapFormState(defaultForecast: ReadonlyArray<DefaultForecast>): Array<Array<string>> {
  const state = new Array<Array<string>>();
  defaultForecast.forEach((row) => {
    state[getMonthNumber(row.month)] = [
      convertMeasure(row.maximum, 'kW').value?.toString() ?? '',
      convertMeasure(row.minimum, 'kW').value?.toString() ?? '',
      convertMeasure(row.auxiliaryConsumption, 'kW').value?.toString() ?? '',
      ...row.forecasts.map((slice) => convertMeasure(slice.value, 'kW').value?.toString() ?? ''),
    ];
  });
  return state;
}

function mapFormStateToForecastInput(
  formState: Array<Array<string>>,
  defaultForecast: ReadonlyArray<DefaultForecast>,
): Array<DefaultForecastInput> {
  return defaultForecast.map((row, rowIndex) => ({
    month: row.month,
    maximum: { value: parseInteger(formState[rowIndex][DataPoint.maximum]), unit: 'kW' },
    minimum: { value: parseInteger(formState[rowIndex][DataPoint.minimum]), unit: 'kW' },
    auxiliaryConsumption: { value: parseInteger(formState[rowIndex][DataPoint.auxiliaryConsumption]), unit: 'kW' },
    forecasts: row.forecasts.map((forecast, forecastIndex) => ({
      value: { value: parseInteger(formState[rowIndex][forecastIndex + DataPoint.production]), unit: 'kW' },
      interval: { start: forecast.interval.start, end: forecast.interval.end },
    })),
  }));
}

const changesAreEqual =
  (change: { month: number; interval: Interval }) => (secondChange: { month: number; interval: Interval }) =>
    change.month === secondChange.month && change.interval === secondChange.interval;

interface AnnualDeviceTableProps {
  deviceId: string;
  defaultForecast: readonly DefaultForecast[];
  installedPower: NullableMeasure;
  powerLevels: readonly NullableMeasure[];
  editable: boolean;
}

export function AnnualDeviceTable({
  deviceId,
  defaultForecast,
  installedPower,
  powerLevels,
  editable,
}: Readonly<AnnualDeviceTableProps>): ReactElement<AnnualDeviceTableProps> {
  const [t] = useTranslation();
  const powerLevelsRules = usePowerLevelsRules(powerLevels, 'kW');
  const { code: locale } = useLocale();
  const { validateMaximum, validateMinimum, validateAuxiliary, validateProduction, warnMaximum, warnMinimum } =
    useDefaultTableRules(installedPower, powerLevelsRules, 'kW');

  const [uneditedFormState, setUneditedFormState] = useState(mapFormState(defaultForecast));
  const [formState, setFormState] = useState(mapFormState(defaultForecast));
  const [errorState, setErrorState] = useState(
    new Array(defaultForecast.length).fill([]).map(() => new Array(7).fill(undefined) as Array<string | undefined>),
  );
  const [warningState, setWarningState] = useState(
    new Array(defaultForecast.length).fill([]).map(() => new Array(2).fill(undefined) as Array<string | undefined>),
  );
  const [changesToBeConfirmed, setChangesToBeConfirmed] = useState<Array<{ month: number; interval: Interval }>>([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  useEffect(() => {
    setUneditedFormState(mapFormState(defaultForecast));
    setFormState(mapFormState(defaultForecast));
    setErrorState(
      new Array(defaultForecast.length).fill([]).map(() => new Array(7).fill(undefined) as Array<string | undefined>),
    );
    setWarningState(
      new Array(defaultForecast.length).fill([]).map(() => new Array(2).fill(undefined) as Array<string | undefined>),
    );
    setChangesToBeConfirmed([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const validateDataPoint = (
    dataPoint: 'maximum' | 'minimum' | 'auxiliaryConsumption' | number,
    forecast: DefaultForecast | DefaultForecastInput,
    value: string,
  ) => {
    if (typeof dataPoint === 'number')
      return { error: validateProduction(forecast).find((rule) => !rule.validate(value))?.message };
    else
      switch (dataPoint) {
        case 'maximum':
          return {
            error: validateMaximum(forecast).find((rule) => !rule.validate(value))?.message,
            warning: warnMaximum(forecast).find((rule) => !rule.validate(value))?.message,
          };
        case 'minimum':
          return {
            error: validateMinimum(forecast).find((rule) => !rule.validate(value))?.message,
            warning: warnMinimum(forecast).find((rule) => !rule.validate(value))?.message,
          };
        case 'auxiliaryConsumption':
          return {
            error: validateAuxiliary(forecast).find((rule) => !rule.validate(value))?.message,
          };
      }
  };

  function trackChanges(
    value: string,
    row: number,
    column: number,
    potentialChange: { month: number; interval: Interval },
  ) {
    setChangesToBeConfirmed((changesToBeConfirmed) => {
      const changes = changesToBeConfirmed.slice();
      if (value === uneditedFormState[row][column])
        changes.splice(changes.findIndex(changesAreEqual(potentialChange)), 1);
      else if (!changesToBeConfirmed.find(changesAreEqual(potentialChange))) changes.push(potentialChange);

      return changes.sort((a, b) =>
        a.month !== b.month
          ? a.month - b.month
          : Number(a.interval.start).valueOf() - Number(b.interval.start).valueOf(),
      );
    });
  }

  const handleChange =
    (dataPoint: 'maximum' | 'minimum' | 'auxiliaryConsumption' | number, forecast: DefaultForecast) =>
    (value: string) => {
      const row = getMonthNumber(forecast.month);
      const column = typeof dataPoint === 'number' ? dataPoint : DataPoint[dataPoint];

      // Update state
      const updatedFormState = deepClone(formState);
      updatedFormState[row][column] = value;
      setFormState(updatedFormState);

      // Validate state
      const validationForecast =
        mapFormStateToForecastInput(updatedFormState, defaultForecast).find((f) => f.month === forecast.month) ??
        forecast;
      const validation = validateDataPoint(dataPoint, validationForecast, value);

      // co-validate state for minimum and maximum data points
      const dependentDatapoint = dataPoint === 'maximum' ? DataPoint['minimum'] : DataPoint['maximum'];
      const dependentValidation = validateDataPoint(
        dependentDatapoint,
        validationForecast,
        updatedFormState[row][dependentDatapoint],
      );

      setErrorState((errorState) => {
        const state = deepClone(errorState);
        state[row][column] = validation.error;

        return state;
      });

      if (dataPoint === 'maximum' || dataPoint === 'minimum') {
        setWarningState((warningState) => {
          const state = deepClone(warningState);
          state[row][column] = validation.warning;

          return state;
        });
        setErrorState((errorState) => {
          const state = deepClone(errorState);
          state[row][dependentDatapoint] = dependentValidation.error;

          return state;
        });
      } else if (typeof dataPoint === 'number') {
        const potentialChange = {
          month: row,
          interval: forecast.forecasts[dataPoint - DataPoint.production].interval,
        };

        trackChanges(value, row, column, potentialChange);
      }
    };

  const [saving, setSaving] = useState(false);
  const [update] = useDefaultForecastMutation();

  const handleSave = () => {
    const state = deepClone(errorState);

    for (const forecast of mapFormStateToForecastInput(formState, defaultForecast)) {
      const row = getMonthNumber(forecast.month);
      state[row][DataPoint.maximum] = validateDataPoint('maximum', forecast, formState[row][DataPoint.maximum]).error;
      state[row][DataPoint.minimum] = validateDataPoint('minimum', forecast, formState[row][DataPoint.minimum]).error;
      state[row][DataPoint.auxiliaryConsumption] = validateDataPoint(
        'auxiliaryConsumption',
        forecast,
        formState[row][DataPoint.auxiliaryConsumption],
      ).error;
      state[row][DataPoint.production] = validateDataPoint(
        DataPoint.production,
        forecast,
        formState[row][DataPoint.production],
      ).error;
      state[row][DataPoint.production + 1] = validateDataPoint(
        DataPoint.production + 1,
        forecast,
        formState[row][DataPoint.production + 1],
      ).error;
      state[row][DataPoint.production + 2] = validateDataPoint(
        DataPoint.production + 2,
        forecast,
        formState[row][DataPoint.production + 2],
      ).error;
      state[row][DataPoint.production + 3] = validateDataPoint(
        DataPoint.production + 3,
        forecast,
        formState[row][DataPoint.production + 3],
      ).error;
    }

    setErrorState(state);

    if (state.some((errors) => errors.some((error) => error))) return;

    if (changesToBeConfirmed.length && !showConfirmationDialog) {
      setShowConfirmationDialog(true);
    } else {
      setSaving(true);
      setShowConfirmationDialog(false);
      setChangesToBeConfirmed([]);
      update(deviceId, mapFormStateToForecastInput(formState, defaultForecast))
        .then(() => setUneditedFormState(deepClone(formState)))
        .catch((error) => console.error(error))
        .finally(() => setSaving(false));
    }
  };

  return (
    <div className="w-full overflow-auto">
      <table className="w-full border-spacing-0 bg-white pb-14">
        <colgroup>
          <col width={`${300 / 17}%`} />
          <col width={`${200 / 17}%`} />
          <col width={`${200 / 17}%`} />
          <col width={`${200 / 17}%`} />
          <col width={`${200 / 17}%`} />
          <col width={`${200 / 17}%`} />
          <col width={`${200 / 17}%`} />
          <col width={`${200 / 17}%`} />
        </colgroup>
        <thead>
          <tr>
            <th
              rowSpan={2}
              className="box-border border-0 border-b border-solid border-divider p-5 text-end text-sm font-medium leading-4"
            >
              {t('SHARED_MONTH')}
            </th>
            <th
              rowSpan={2}
              className="box-border border-0 border-b border-l border-solid border-divider p-5 text-sm font-medium leading-4"
            >
              {t('ANNUAL_PLANNING_MAXIMUM')}
            </th>
            <th
              rowSpan={2}
              className="box-border border-0 border-b border-l border-solid border-divider p-5 text-sm font-medium leading-4"
            >
              {t('ANNUAL_PLANNING_MINIMUM')}
            </th>
            <th
              rowSpan={2}
              className="box-border border-0 border-b border-l border-solid border-divider p-5 text-sm font-medium leading-4"
            >
              {t('ANNUAL_PLANNING_AUXILIARY_CONSUMPTION')}
            </th>
            <th
              colSpan={4}
              className="box-border border-0 border-b border-l border-solid border-divider px-5 py-2 text-sm font-medium leading-4"
            >
              {t('ANNUAL_PLANNING_PRODUCTION')}
            </th>
          </tr>
          <tr>
            {[
              { startDay: new Date('1970-01-05T08:00'), endNight: new Date('1970-01-09T20:00') },
              { startDay: new Date('1970-01-03T08:00'), endNight: new Date('1970-01-04T20:00') },
            ].map(({ startDay, endNight }) => (
              <Fragment key={startDay.toISOString()}>
                <th className="box-border whitespace-pre border-0 border-b border-l border-solid border-divider px-5 py-4 text-sm font-medium leading-4">{`${startDay.toLocaleDateString(
                  locale,
                  {
                    weekday: 'short',
                  },
                )} - ${endNight.toLocaleDateString(locale, {
                  weekday: 'short',
                })}\n(${startDay.toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })} - ${endNight.toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })})`}</th>
                <th className="box-border whitespace-pre border-0 border-b border-l border-solid border-divider px-5 py-4 text-sm font-medium leading-4">{`${startDay.toLocaleDateString(
                  locale,
                  {
                    weekday: 'short',
                  },
                )} - ${endNight.toLocaleDateString(locale, {
                  weekday: 'short',
                })}\n(${endNight.toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })} - ${startDay.toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                })})`}</th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {defaultForecast.map((row) => (
            <tr
              key={row.month}
              className="shadow-black/50 odd:bg-grey-50 first-of-type:shadow-[inset_0_2px_4px_-4px_var(--tw-shadow-color)]"
            >
              <th
                scope="row"
                className="box-border border-0 border-b border-solid border-grey-200 p-5 text-end text-sm font-medium leading-4"
              >
                <span className="lg:hidden">
                  {new Date(new Date('1970-01-01').setMonth(getMonthNumber(row.month))).toLocaleDateString(locale, {
                    month: 'short',
                  })}
                </span>
                <span className="hidden lg:inline">
                  {new Date(new Date('1970-01-01').setMonth(getMonthNumber(row.month))).toLocaleDateString(locale, {
                    month: 'long',
                  })}
                </span>
              </th>
              <AnnualDeviceTableCell
                value={formState[getMonthNumber(row.month)][DataPoint.maximum]}
                error={errorState[getMonthNumber(row.month)][DataPoint.maximum]}
                warning={warningState[getMonthNumber(row.month)][DataPoint.maximum]}
                edited={
                  formState[getMonthNumber(row.month)][DataPoint.maximum] !==
                  uneditedFormState[getMonthNumber(row.month)][DataPoint.maximum]
                }
                disabled={!editable || saving}
                handleChange={handleChange('maximum', row)}
              />
              <AnnualDeviceTableCell
                value={formState[getMonthNumber(row.month)][DataPoint.minimum]}
                error={errorState[getMonthNumber(row.month)][DataPoint.minimum]}
                warning={warningState[getMonthNumber(row.month)][DataPoint.minimum]}
                edited={
                  formState[getMonthNumber(row.month)][DataPoint.minimum] !==
                  uneditedFormState[getMonthNumber(row.month)][DataPoint.minimum]
                }
                disabled={!editable || saving}
                handleChange={handleChange('minimum', row)}
              />
              <AnnualDeviceTableCell
                value={formState[getMonthNumber(row.month)][DataPoint.auxiliaryConsumption]}
                error={errorState[getMonthNumber(row.month)][DataPoint.auxiliaryConsumption]}
                edited={
                  formState[getMonthNumber(row.month)][DataPoint.auxiliaryConsumption] !==
                  uneditedFormState[getMonthNumber(row.month)][DataPoint.auxiliaryConsumption]
                }
                disabled={!editable || saving}
                handleChange={handleChange('auxiliaryConsumption', row)}
              />
              {row.forecasts.map((production, productionIndex) => {
                const dataPoint = DataPoint.production + productionIndex;
                return (
                  <AnnualDeviceTableCell
                    key={production.interval.start.toISOString()}
                    value={formState[getMonthNumber(row.month)][dataPoint]}
                    error={errorState[getMonthNumber(row.month)][dataPoint]}
                    edited={
                      formState[getMonthNumber(row.month)][dataPoint] !==
                      uneditedFormState[getMonthNumber(row.month)][dataPoint]
                    }
                    disabled={!editable || saving}
                    handleChange={handleChange(dataPoint, row)}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <SaveBar
        open={JSON.stringify(formState) !== JSON.stringify(uneditedFormState)}
        isValid={!errorState.some((errors) => errors.some((error) => error))}
        saving={saving}
        onCancel={() => {
          setFormState(mapFormState(defaultForecast));
          setErrorState(
            new Array(defaultForecast.length)
              .fill([])
              .map(() => new Array(7).fill(undefined) as Array<string | undefined>),
          );
          setWarningState(
            new Array(defaultForecast.length)
              .fill([])
              .map(() => new Array(2).fill(undefined) as Array<string | undefined>),
          );
          setChangesToBeConfirmed([]);
        }}
        onSave={handleSave}
      />
      <Dialog open={showConfirmationDialog} onClose={() => setShowConfirmationDialog(false)}>
        <DialogTitle>{t('ANNUAL_PLANNING_CONFIRMATION_DIALOG_TITLE')}</DialogTitle>
        <DialogContent>
          <p>{t('ANNUAL_PLANNING_CONFIRMATION_DIALOG_DESCRIPTION_PREFIX')}</p>
          {changesToBeConfirmed.map((change, changeIndex) => {
            const newMonth = changeIndex ? change.month !== changesToBeConfirmed[changeIndex - 1].month : true;
            return (
              <Fragment key={`${change.month}-${change.interval.start.toString()}-${change.interval.end.toString()}`}>
                {newMonth && (
                  <p className="mb-0 mt-2 first-of-type:mt-0">
                    <Trans
                      values={{
                        month: new Date(new Date().setMonth(change.month)).toLocaleDateString(locale, {
                          month: 'long',
                        }),
                      }}
                      components={{ strong: <strong /> }}
                    >
                      ANNUAL_PLANNING_CONFIRMATION_DIALOG_MONTH_SNIPPET
                    </Trans>
                  </p>
                )}
                <p className="m-0">
                  <Trans
                    values={{
                      dayStart: new Date(change.interval.start).toLocaleDateString(locale, {
                        weekday: 'long',
                      }),
                      dayEnd: new Date(change.interval.end).toLocaleDateString(locale, {
                        weekday: 'long',
                      }),
                      timeStart: new Date(change.interval.start).toLocaleTimeString(locale, {
                        hour: '2-digit',
                        hour12: false,
                      }),
                      timeEnd: new Date(change.interval.end).toLocaleTimeString(locale, {
                        hour: '2-digit',
                        hour12: false,
                      }),
                    }}
                    components={{ strong: <strong /> }}
                  >
                    ANNUAL_PLANNING_CONFIRMATION_DIALOG_TIME_SNIPPET
                  </Trans>
                </p>
              </Fragment>
            );
          })}
          <p>{t('ANNUAL_PLANNING_CONFIRMATION_DIALOG_DESCRIPTION_SUFFIX')}</p>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="cancel-production-change"
            variant="outlined"
            onClick={() => setShowConfirmationDialog(false)}
          >
            {t('SHARED_CANCEL')}
          </Button>
          <Button onClick={handleSave}>{t('ANNUAL_PLANNING_CONFIRMATION_BUTTON')}</Button>
        </DialogActions>
      </Dialog>
      <AbortConfirmationDialog when={JSON.stringify(formState) !== JSON.stringify(uneditedFormState)} />
    </div>
  );
}
